/**
 * Live Training preferences (browser-local) + session-forced client constraints.
 *
 * Coach session prefs are per training session (ts_id).
 * Client device prefs are global on this browser / WebView.
 * Forced constraints from the coach override device prefs when active.
 */

import { AUTO_SEQUENCE_TRANSITION_PAUSE_SEC } from "./sequenceTransitionPause";
import { normalizeTablePhoneCoverage } from "./tablePhoneCoverage";

export const LIVE_TRAINING_CLIENT_SETTINGS_KEY = "smarttt.liveTrainingClientSettings";
export const LIVE_TRAINING_COACH_SESSION_PREFIX = "smarttt.liveTrainingCoachSession_";
export const LIVE_TRAINING_FORCED_CONSTRAINTS_PREFIX =
  "smarttt.liveTrainingForcedConstraints_";

/** Legacy key used by Training.jsx / startExercise payloads. */
export const AUTO_ADVANCE_SEQUENCES_PREFIX = "smarttt_autoAdvanceSequences_";

export const RECORDING_FPS_OPTIONS = Object.freeze([30, 60, 120]);
export const SEQUENCE_PAUSE_SEC_OPTIONS = Object.freeze([10, 15, 20, 30]);
export const AUTOEVAL_VIDEO_DURATION_SEC_OPTIONS = Object.freeze([15, 30, 45, 60]);
/** Evaluation upload encode tiers (H.264). Default low ≈ 8 MB/min @ 720p. */
export const VIDEO_QUALITY_OPTIONS = Object.freeze(["low", "medium", "high"]);
/** Table-replay bitrate tiers (H.264). High is eval-only — replay stays lean. */
export const REPLAY_VIDEO_QUALITY_OPTIONS = Object.freeze(["low", "medium"]);
/** Last-10s replay ring FPS (eval/KoT may still use 120 on Pro). */
export const REPLAY_FPS_OPTIONS = Object.freeze([30, 60]);
/** Table-replay encode size. Default 480p for a fast last-10s upload. */
export const RECORDING_RESOLUTION_OPTIONS = Object.freeze([
  "480p",
  "720p",
  "1080p",
]);
/** Base (low) bitrate per resolution; medium is ~2×. */
export const RECORDING_RESOLUTION_PRESETS = Object.freeze({
  "480p": Object.freeze({ width: 854, height: 480, bitrate: 450_000 }),
  "720p": Object.freeze({ width: 1280, height: 720, bitrate: 900_000 }),
  "1080p": Object.freeze({ width: 1920, height: 1080, bitrate: 1_500_000 }),
});
export const TABLE_REPLAY_WINDOW_SEC = 10;
/** Camera table position for evaluation clips (geometry / filename). */
export const CAMERA_VIEW_OPTIONS = Object.freeze(["spectator", "referee"]);

export const DEFAULT_COACH_SESSION_SETTINGS = Object.freeze({
  version: 1,
  /** When true, next sequence block starts after transition pause. */
  autoAdvanceSequences: false,
  /** Rest between auto-advanced sequence blocks (seconds). */
  sequenceTransitionPauseSec: AUTO_SEQUENCE_TRANSITION_PAUSE_SEC,
  /** When true, player devices record evaluation clips (720p 60fps H.264). */
  evalVideoRecordingEnabled: true,
  /**
   * When true, the coach phone also runs eval capture on this session.
   * Off by default — coach devices should not record unless explicitly enabled.
   */
  coachDeviceEvalRecordingEnabled: false,
  /** When true, connected clients must use coach FPS + autoeval duration + video quality. */
  forceClientCaptureSettings: false,
  /** FPS forced / recommended for player capture on this session. */
  recommendedRecordingFps: 60,
  /** Autoeval clip max duration forced on connected clients (seconds). */
  forcedAutoevalVideoMaxDurationSec: 60,
  /** Eval upload quality forced on connected clients (H.264). */
  forcedVideoQuality: "low",
  /** Last-10s table replay ring on player cameras. On by default. */
  instantReplayEnabled: true,
  /** Replay frame size. Default 480p for a fast coach clip. */
  recordingResolution: "480p",
  /** Replay encode bitrate tier (low / medium). */
  replayVideoQuality: "low",
  /** Replay ring frame rate (30 or 60). */
  replayRecordingFps: 60,
});

export const DEFAULT_CLIENT_SETTINGS = Object.freeze({
  version: 1,
  /**
   * When true, this player device records evaluation / KoT video.
   * Independent of the coach session master switch (both must be on).
   */
  cameraRecordingEnabled: true,
  /** Preferred capture FPS on this device (player phone / WebView). */
  recordingFps: 60,
  /** Max length for player auto-evaluation clip (seconds). */
  autoevalVideoMaxDurationSec: 60,
  /** Preferred eval upload quality on this device (H.264). */
  videoQuality: "low",
  /** Table camera position: spectator (end) or referee (long side). */
  cameraView: "spectator",
  /**
   * When true, this device stays on `tablePhoneNumber` and records whoever
   * is seated there (club Admin iOS phone), even if this player moves.
   */
  tablePhoneEnabled: false,
  /** Main table: whose seated players get evaluation / KoT clips. */
  tablePhoneNumber: null,
  /**
   * Every table this phone films (main included). A wide-angle phone can
   * cover several tables; the coach Record button works on each of them.
   */
  tablePhoneNumbers: Object.freeze([]),
  /** Court end this phone faces: A or B. */
  tablePhoneSide: "A",
  /**
   * When true, this device is the camera for a whole training station
   * (subgroup / tsg), not a single table. Used for off-table chrono and
   * coach-requested station recordings.
   */
  stationPhoneEnabled: false,
  stationPhoneTsgId: null,
  /**
   * When true (default), large video uploads are blocked on cellular.
   * Turn off in Live Training settings to allow mobile data uploads.
   */
  wifiOnlyVideoUpload: true,
});

function normalizeCameraView(raw, fallback = "spectator") {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (CAMERA_VIEW_OPTIONS.includes(s)) return s;
  if (s.startsWith("ref")) return "referee";
  if (s.startsWith("spec")) return "spectator";
  return fallback;
}

function clampToOptions(raw, options, fallback) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  if (options.includes(n)) return n;
  let best = options[0];
  let bestDist = Math.abs(n - best);
  for (let i = 1; i < options.length; i++) {
    const d = Math.abs(n - options[i]);
    if (d < bestDist) {
      best = options[i];
      bestDist = d;
    }
  }
  return best;
}

function normalizeRecordingResolution(raw, fallback = "480p") {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (RECORDING_RESOLUTION_OPTIONS.includes(s)) return s;
  if (s === "480" || s === "854x480") return "480p";
  if (s === "720" || s === "1280x720") return "720p";
  if (s === "1080" || s === "1920x1080") return "1080p";
  return fallback;
}

function normalizeVideoQuality(raw, fallback = "low") {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (VIDEO_QUALITY_OPTIONS.includes(s)) return s;
  return fallback;
}

function normalizeReplayVideoQuality(raw, fallback = "low") {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (s === "med") return "medium";
  if (REPLAY_VIDEO_QUALITY_OPTIONS.includes(s)) return s;
  return fallback;
}

/** H.264 target bitrate for the last-10s replay ring. */
export function replayBitrateFor(resolution, quality = "low") {
  const preset =
    RECORDING_RESOLUTION_PRESETS[normalizeRecordingResolution(resolution)] ||
    RECORDING_RESOLUTION_PRESETS["480p"];
  const tier = normalizeReplayVideoQuality(quality);
  return tier === "medium"
    ? Math.round(preset.bitrate * 2)
    : preset.bitrate;
}

function parseBool(raw, fallback) {
  if (raw === true || raw === "true" || raw === 1 || raw === "1") return true;
  if (raw === false || raw === "false" || raw === 0 || raw === "0") return false;
  return fallback;
}

export function recordingFpsOptionsForLicense(features) {
  if (features?.mobile_app_120fps) return [...RECORDING_FPS_OPTIONS];
  return [30, 60];
}

export function licenseAllows120Fps() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return false;
    const u = JSON.parse(raw);
    return Boolean(
      u?.club_license?.features?.mobile_app_120fps ||
        u?.club_license?.player_features?.includes?.("mobile_app_120fps")
    );
  } catch {
    return false;
  }
}

export function capRecordingFps(fps, features) {
  return clampToOptions(
    fps,
    recordingFpsOptionsForLicense(features || { mobile_app_120fps: licenseAllows120Fps() }),
    DEFAULT_CLIENT_SETTINGS.recordingFps
  );
}

export function normalizeCoachSessionSettings(input) {
  const src = input && typeof input === "object" ? input : {};
  const forceRaw =
    src.forceClientCaptureSettings ?? src.force_client_capture;
  return {
    version: 1,
    autoAdvanceSequences: src.autoAdvanceSequences === true,
    sequenceTransitionPauseSec: clampToOptions(
      src.sequenceTransitionPauseSec ?? src.sequence_transition_pause_sec,
      SEQUENCE_PAUSE_SEC_OPTIONS,
      DEFAULT_COACH_SESSION_SETTINGS.sequenceTransitionPauseSec
    ),
    evalVideoRecordingEnabled: parseBool(
      src.evalVideoRecordingEnabled ?? src.eval_video_recording_enabled,
      DEFAULT_COACH_SESSION_SETTINGS.evalVideoRecordingEnabled
    ),
    coachDeviceEvalRecordingEnabled: parseBool(
      src.coachDeviceEvalRecordingEnabled ??
        src.coach_device_eval_recording_enabled,
      DEFAULT_COACH_SESSION_SETTINGS.coachDeviceEvalRecordingEnabled
    ),
    forceClientCaptureSettings:
      forceRaw === true || forceRaw === "true" || forceRaw === 1,
    recommendedRecordingFps: capRecordingFps(
      src.recommendedRecordingFps ??
        src.recommended_recording_fps ??
        src.recordingFps ??
        src.recording_fps
    ),
    forcedAutoevalVideoMaxDurationSec: clampToOptions(
      src.forcedAutoevalVideoMaxDurationSec ??
        src.forced_autoeval_video_max_duration_sec ??
        src.autoevalVideoMaxDurationSec ??
        src.autoeval_video_max_duration_sec,
      AUTOEVAL_VIDEO_DURATION_SEC_OPTIONS,
      DEFAULT_COACH_SESSION_SETTINGS.forcedAutoevalVideoMaxDurationSec
    ),
    forcedVideoQuality: normalizeVideoQuality(
      src.forcedVideoQuality ??
        src.forced_video_quality ??
        src.videoQuality ??
        src.video_quality,
      DEFAULT_COACH_SESSION_SETTINGS.forcedVideoQuality
    ),
    instantReplayEnabled: parseBool(
      src.instantReplayEnabled ?? src.instant_replay_enabled,
      DEFAULT_COACH_SESSION_SETTINGS.instantReplayEnabled
    ),
    recordingResolution: normalizeRecordingResolution(
      src.recordingResolution ?? src.recording_resolution,
      DEFAULT_COACH_SESSION_SETTINGS.recordingResolution
    ),
    replayVideoQuality: normalizeReplayVideoQuality(
      src.replayVideoQuality ??
        src.replay_video_quality ??
        src.recordingBitrate ??
        src.recording_bitrate,
      DEFAULT_COACH_SESSION_SETTINGS.replayVideoQuality
    ),
    replayRecordingFps: clampToOptions(
      src.replayRecordingFps ??
        src.replay_recording_fps ??
        src.replayFps ??
        src.replay_fps,
      REPLAY_FPS_OPTIONS,
      DEFAULT_COACH_SESSION_SETTINGS.replayRecordingFps
    ),
  };
}

export function normalizeClientSettings(input) {
  const src = input && typeof input === "object" ? input : {};
  const tablePhone = normalizeTablePhoneCoverage(
    src.tablePhoneNumber ?? src.table_phone_number,
    src.tablePhoneNumbers ?? src.table_phone_numbers
  );
  return {
    version: 1,
    cameraRecordingEnabled: parseBool(
      src.cameraRecordingEnabled ?? src.camera_recording_enabled,
      DEFAULT_CLIENT_SETTINGS.cameraRecordingEnabled
    ),
    recordingFps: capRecordingFps(src.recordingFps ?? src.recording_fps),
    autoevalVideoMaxDurationSec: clampToOptions(
      src.autoevalVideoMaxDurationSec ?? src.autoeval_video_max_duration_sec,
      AUTOEVAL_VIDEO_DURATION_SEC_OPTIONS,
      DEFAULT_CLIENT_SETTINGS.autoevalVideoMaxDurationSec
    ),
    videoQuality: normalizeVideoQuality(
      src.videoQuality ?? src.video_quality,
      DEFAULT_CLIENT_SETTINGS.videoQuality
    ),
    cameraView: normalizeCameraView(
      src.cameraView ?? src.camera_view ?? src.cameraPositionView,
      DEFAULT_CLIENT_SETTINGS.cameraView
    ),
    tablePhoneEnabled: parseBool(
      src.tablePhoneEnabled ?? src.table_phone_enabled,
      DEFAULT_CLIENT_SETTINGS.tablePhoneEnabled
    ),
    tablePhoneNumber: tablePhone.main,
    tablePhoneNumbers: tablePhone.tables,
    tablePhoneSide: (() => {
      const side = String(src.tablePhoneSide ?? src.table_phone_side ?? "A")
        .trim()
        .toUpperCase();
      return side === "B" ? "B" : "A";
    })(),
    stationPhoneEnabled: parseBool(
      src.stationPhoneEnabled ?? src.station_phone_enabled,
      DEFAULT_CLIENT_SETTINGS.stationPhoneEnabled
    ),
    stationPhoneTsgId: (() => {
      const id = src.stationPhoneTsgId ?? src.station_phone_tsg_id;
      if (id == null || String(id).trim() === "") return null;
      return String(id).trim();
    })(),
    wifiOnlyVideoUpload: parseBool(
      src.wifiOnlyVideoUpload ?? src.wifi_only_video_upload,
      DEFAULT_CLIENT_SETTINGS.wifiOnlyVideoUpload
    ),
  };
}

export function normalizeForcedConstraints(input) {
  const src = input && typeof input === "object" ? input : {};
  const forceRaw =
    src.force_client_capture ?? src.forceClientCaptureSettings;
  return {
    version: 1,
    forceClientCaptureSettings:
      forceRaw === true || forceRaw === "true" || forceRaw === 1,
    evalVideoRecordingEnabled: parseBool(
      src.eval_video_recording_enabled ?? src.evalVideoRecordingEnabled,
      true
    ),
    recordingFps: capRecordingFps(
      src.recording_fps ?? src.recordingFps ?? src.recommendedRecordingFps
    ),
    autoevalVideoMaxDurationSec: clampToOptions(
      src.autoeval_video_max_duration_sec ??
        src.autoevalVideoMaxDurationSec ??
        src.forcedAutoevalVideoMaxDurationSec,
      AUTOEVAL_VIDEO_DURATION_SEC_OPTIONS,
      DEFAULT_COACH_SESSION_SETTINGS.forcedAutoevalVideoMaxDurationSec
    ),
    videoQuality: normalizeVideoQuality(
      src.video_quality ??
        src.videoQuality ??
        src.forcedVideoQuality ??
        src.forced_video_quality,
      DEFAULT_COACH_SESSION_SETTINGS.forcedVideoQuality
    ),
    instantReplayEnabled: parseBool(
      src.instant_replay_enabled ?? src.instantReplayEnabled,
      DEFAULT_COACH_SESSION_SETTINGS.instantReplayEnabled
    ),
    recordingResolution: normalizeRecordingResolution(
      src.recording_resolution ?? src.recordingResolution,
      DEFAULT_COACH_SESSION_SETTINGS.recordingResolution
    ),
    replayVideoQuality: normalizeReplayVideoQuality(
      src.replay_video_quality ??
        src.replayVideoQuality ??
        src.recording_bitrate ??
        src.recordingBitrate,
      DEFAULT_COACH_SESSION_SETTINGS.replayVideoQuality
    ),
    replayRecordingFps: clampToOptions(
      src.replay_recording_fps ??
        src.replayRecordingFps ??
        src.replay_fps ??
        src.replayFps,
      REPLAY_FPS_OPTIONS,
      DEFAULT_COACH_SESSION_SETTINGS.replayRecordingFps
    ),
    updatedAt: src.updated_at || src.updatedAt || null,
  };
}

function readLegacyAutoAdvance(tsId) {
  if (!tsId || typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(`${AUTO_ADVANCE_SEQUENCES_PREFIX}${tsId}`);
    if (v === null) return null;
    return v === "true";
  } catch {
    return null;
  }
}

function writeLegacyAutoAdvance(tsId, enabled) {
  if (!tsId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${AUTO_ADVANCE_SEQUENCES_PREFIX}${tsId}`,
      enabled ? "true" : "false"
    );
  } catch {
    /* ignore */
  }
}

export function loadCoachSessionSettings(tsId) {
  const defaults = { ...DEFAULT_COACH_SESSION_SETTINGS };
  if (!tsId || typeof window === "undefined") return defaults;
  try {
    const raw = window.localStorage.getItem(
      `${LIVE_TRAINING_COACH_SESSION_PREFIX}${tsId}`
    );
    if (raw) {
      const normalized = normalizeCoachSessionSettings(JSON.parse(raw));
      writeLegacyAutoAdvance(tsId, normalized.autoAdvanceSequences);
      return normalized;
    }
  } catch {
    /* fall through */
  }
  const legacy = readLegacyAutoAdvance(tsId);
  if (legacy !== null) {
    return normalizeCoachSessionSettings({
      ...defaults,
      autoAdvanceSequences: legacy,
    });
  }
  return defaults;
}

export function saveCoachSessionSettings(tsId, next) {
  const normalized = normalizeCoachSessionSettings(next);
  if (tsId && typeof window !== "undefined") {
    try {
      window.localStorage.setItem(
        `${LIVE_TRAINING_COACH_SESSION_PREFIX}${tsId}`,
        JSON.stringify(normalized)
      );
    } catch {
      /* ignore */
    }
    writeLegacyAutoAdvance(tsId, normalized.autoAdvanceSequences);
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("smarttt:live-training-coach-settings", {
        detail: { tsId, settings: normalized },
      })
    );
  }
  return normalized;
}

export function loadClientSettings() {
  if (typeof window === "undefined") {
    return { ...DEFAULT_CLIENT_SETTINGS };
  }
  try {
    const raw = window.localStorage.getItem(LIVE_TRAINING_CLIENT_SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_CLIENT_SETTINGS };
    return normalizeClientSettings(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_CLIENT_SETTINGS };
  }
}

export function saveClientSettings(next) {
  const normalized = normalizeClientSettings(next);
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(
        LIVE_TRAINING_CLIENT_SETTINGS_KEY,
        JSON.stringify(normalized)
      );
    } catch {
      /* ignore */
    }
    window.dispatchEvent(
      new CustomEvent("smarttt:live-training-client-settings", {
        detail: normalized,
      })
    );
  }
  return normalized;
}

export function loadForcedConstraints(tsId) {
  if (!tsId || typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(
      `${LIVE_TRAINING_FORCED_CONSTRAINTS_PREFIX}${tsId}`
    );
    if (!raw) return null;
    return normalizeForcedConstraints(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function applyForcedConstraints(tsId, payload) {
  if (!tsId) return null;
  const normalized = normalizeForcedConstraints(payload);
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem(
        `${LIVE_TRAINING_FORCED_CONSTRAINTS_PREFIX}${tsId}`,
        JSON.stringify(normalized)
      );
    } catch {
      /* ignore */
    }
    window.dispatchEvent(
      new CustomEvent("smarttt:live-training-forced-constraints", {
        detail: { tsId, constraints: normalized },
      })
    );
  }
  return normalized;
}

/**
 * Effective capture settings for this device in a session.
 * Autoeval clip length follows the coach session setting whenever constraints
 * have been published (same as the eval-recording master switch). FPS / quality
 * stay local unless the coach also enabled force-client-capture.
 */
export function getEffectiveClientCaptureSettings(tsId) {
  const local = loadClientSettings();
  const forced = loadForcedConstraints(tsId);
  const sessionAutoevalSec =
    forced && Number.isFinite(Number(forced.autoevalVideoMaxDurationSec))
      ? Number(forced.autoevalVideoMaxDurationSec)
      : local.autoevalVideoMaxDurationSec;

  if (forced?.forceClientCaptureSettings) {
    return {
      recordingFps: forced.recordingFps,
      autoevalVideoMaxDurationSec: forced.autoevalVideoMaxDurationSec,
      videoQuality: forced.videoQuality,
      cameraView: local.cameraView || DEFAULT_CLIENT_SETTINGS.cameraView,
      cameraRecordingEnabled: local.cameraRecordingEnabled !== false,
      evalVideoRecordingEnabled: forced.evalVideoRecordingEnabled !== false,
      instantReplayEnabled: forced.instantReplayEnabled !== false,
      recordingResolution:
        forced.recordingResolution ||
        DEFAULT_COACH_SESSION_SETTINGS.recordingResolution,
      replayVideoQuality:
        forced.replayVideoQuality ||
        DEFAULT_COACH_SESSION_SETTINGS.replayVideoQuality,
      replayRecordingFps:
        forced.replayRecordingFps ||
        DEFAULT_COACH_SESSION_SETTINGS.replayRecordingFps,
      forced: true,
      forceClientCaptureSettings: true,
    };
  }
  return {
    ...local,
    autoevalVideoMaxDurationSec: sessionAutoevalSec,
    cameraRecordingEnabled: local.cameraRecordingEnabled !== false,
    evalVideoRecordingEnabled: isEvalVideoRecordingEnabled(tsId),
    instantReplayEnabled: isInstantReplayEnabled(tsId),
    recordingResolution: replayRecordingResolution(tsId),
    replayVideoQuality: replayVideoQuality(tsId),
    replayRecordingFps: replayRecordingFps(tsId),
    forced: false,
    forceClientCaptureSettings: false,
  };
}

/**
 * Session master switch for evaluation clips. Defaults on.
 * Coach disable is always applied (independent of force-capture lock).
 */
export function isEvalVideoRecordingEnabled(tsId) {
  const forced = loadForcedConstraints(tsId);
  if (forced && typeof forced.evalVideoRecordingEnabled === "boolean") {
    return forced.evalVideoRecordingEnabled !== false;
  }
  return loadCoachSessionSettings(tsId).evalVideoRecordingEnabled !== false;
}

/** Session switch for the last-10s table replay ring. Defaults on. */
export function isInstantReplayEnabled(tsId) {
  const forced = loadForcedConstraints(tsId);
  if (forced && typeof forced.instantReplayEnabled === "boolean") {
    return forced.instantReplayEnabled !== false;
  }
  return loadCoachSessionSettings(tsId).instantReplayEnabled !== false;
}

export function replayRecordingResolution(tsId) {
  const forced = loadForcedConstraints(tsId);
  if (forced?.recordingResolution) return forced.recordingResolution;
  return (
    loadCoachSessionSettings(tsId).recordingResolution ||
    DEFAULT_COACH_SESSION_SETTINGS.recordingResolution
  );
}

export function replayVideoQuality(tsId) {
  const forced = loadForcedConstraints(tsId);
  if (forced?.replayVideoQuality) return forced.replayVideoQuality;
  return (
    loadCoachSessionSettings(tsId).replayVideoQuality ||
    DEFAULT_COACH_SESSION_SETTINGS.replayVideoQuality
  );
}

export function replayRecordingFps(tsId) {
  const forced = loadForcedConstraints(tsId);
  if (forced?.replayRecordingFps) {
    return clampToOptions(
      forced.replayRecordingFps,
      REPLAY_FPS_OPTIONS,
      DEFAULT_COACH_SESSION_SETTINGS.replayRecordingFps
    );
  }
  return clampToOptions(
    loadCoachSessionSettings(tsId).replayRecordingFps,
    REPLAY_FPS_OPTIONS,
    DEFAULT_COACH_SESSION_SETTINGS.replayRecordingFps
  );
}

/**
 * Encode targets for the 10s replay ring.
 * Coach sets resolution, bitrate tier, and FPS (30/60) for the session.
 */
export function replayEncodeSettings(tsId) {
  const resolution = normalizeRecordingResolution(
    replayRecordingResolution(tsId)
  );
  const quality = normalizeReplayVideoQuality(replayVideoQuality(tsId));
  const preset =
    RECORDING_RESOLUTION_PRESETS[resolution] ||
    RECORDING_RESOLUTION_PRESETS["480p"];
  const fps = replayRecordingFps(tsId);
  return {
    enabled: isInstantReplayEnabled(tsId),
    resolution,
    quality,
    width: preset.width,
    height: preset.height,
    bitrate: replayBitrateFor(resolution, quality),
    fps,
    windowSec: TABLE_REPLAY_WINDOW_SEC,
  };
}

/** Coach phone capture — opt-in only (defaults off). */
export function isCoachDeviceEvalRecordingEnabled(tsId) {
  return loadCoachSessionSettings(tsId).coachDeviceEvalRecordingEnabled === true;
}

/** This player device — local camera recording switch (defaults on). */
export function isClientCameraRecordingEnabled() {
  return loadClientSettings().cameraRecordingEnabled !== false;
}

/**
 * Whether this player device should capture eval video right now.
 * Requires the coach session switch and the local device switch.
 */
export function isPlayerDeviceEvalCaptureEnabled(tsId) {
  return (
    isEvalVideoRecordingEnabled(tsId) && isClientCameraRecordingEnabled()
  );
}

/**
 * Whether THIS phone should run live-training eval capture right now.
 * Coach UI must follow "Record on this coach phone" (opt-in).
 * Player UI follows session player recording + this-device camera switch.
 *
 * Use isCoachView (training-room coach UI), not the Home role raw string —
 * those can diverge for dual-role accounts and incorrectly open the player path.
 */
export function isLiveTrainingDeviceEvalCaptureEnabled(
  tsId,
  { isCoachView = false } = {}
) {
  if (isCoachView) return isCoachDeviceEvalRecordingEnabled(tsId);
  // Warm the player camera for eval clips and/or the last-10s replay ring.
  return (
    isClientCameraRecordingEnabled() &&
    (isEvalVideoRecordingEnabled(tsId) || isInstantReplayEnabled(tsId))
  );
}

/** Payload for startExerciseWithSequences. */
export function buildAutoSequenceStartPayload(tsId) {
  const s = loadCoachSessionSettings(tsId);
  return {
    auto_advance_sequences: s.autoAdvanceSequences === true,
    auto_sequence_pause_sec: s.sequenceTransitionPauseSec,
  };
}

/** Socket payload for setLiveTrainingClientConstraints. */
export function buildClientConstraintsSocketPayload(tsId, coachSettings = null) {
  const s = coachSettings
    ? normalizeCoachSessionSettings(coachSettings)
    : loadCoachSessionSettings(tsId);
  return {
    ts_id: tsId != null ? String(tsId) : null,
    force_client_capture: s.forceClientCaptureSettings === true,
    eval_video_recording_enabled: s.evalVideoRecordingEnabled !== false,
    recording_fps: s.recommendedRecordingFps,
    autoeval_video_max_duration_sec: s.forcedAutoevalVideoMaxDurationSec,
    video_quality: s.forcedVideoQuality,
    instant_replay_enabled: s.instantReplayEnabled !== false,
    recording_resolution: s.recordingResolution || "480p",
    replay_video_quality: s.replayVideoQuality || "low",
    replay_recording_fps: s.replayRecordingFps || 60,
  };
}

/**
 * Publish coach capture constraints to the session room.
 * Safe no-op if socket / tsId missing.
 */
export function publishLiveTrainingClientConstraints(socket, tsId, coachSettings) {
  if (!socket || !tsId) return;
  const payload = buildClientConstraintsSocketPayload(tsId, coachSettings);
  socket.emit("setLiveTrainingClientConstraints", payload, (ack) => {
    if (ack?.ok === false) {
      console.warn("setLiveTrainingClientConstraints failed:", ack?.code);
      return;
    }
    if (ack?.ok) {
      applyForcedConstraints(tsId, ack);
    }
  });
}
